import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'
import { UserState } from '../stores/userStore'
import { UserData } from '@/types/userTypes'
// Import the theme store so applyTheme() runs on load (before first paint) and
// the saved light/dark choice is honoured.
import '../stores/themeStore'

interface RouterContext {
  queryClient: QueryClient
  userStore: {
    setState: (user: UserData) => void
    getState: () => UserState
  }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  // Rehydrate the signed-in user from the server session on load so a page
  // refresh doesn't bounce the user back to login. This MUST run in beforeLoad
  // (not loader): a parent's beforeLoad is awaited before any child's beforeLoad,
  // whereas route loaders run in parallel — so hydrating here guarantees the
  // store is populated before child routes run their auth guard. Child routes
  // still guard themselves against a null user (redirecting to /authentication).
  beforeLoad: async ({ context }) => {
    if (context.userStore.getState().User != null) return
    try {
      const response = await fetch('/api/me', { credentials: 'same-origin' })
      if (response.ok) {
        const user = (await response.json()) as UserData
        if (user && user.id) {
          context.userStore.setState(user)
        }
      }
    } catch {
      // Ignore — unauthenticated users are redirected by child route guards.
    }
  },
})

function RootLayout() {
  return (
    <div className="h-full bg-background text-foreground">
      <Outlet />
    </div>
  )
}
